/**
 * Task evidence API client.
 *
 * Deliberately not built on `fetcher`: it forces `Content-Type: application/json` (which breaks
 * multipart upload) and collapses every error into `Error(detail)`, while the evidence endpoints
 * return a structured `{code, message, detail, retryable}` body the UI has to act on.
 */
import { getAuthToken, handleUnauthorized, resolveApiUrl } from '../utils/fetcher';
import type {
  EvidenceAccessAction,
  EvidenceAccessResponse,
  EvidenceDeleteResponse,
  EvidenceErrorPayload,
  EvidenceListResponse,
  EvidenceUploadResponse,
} from '../types/evidence';

// Client-side pre-check limits. These mirror the backend defaults
// (EVIDENCE_MAX_UPLOAD_BYTES / EVIDENCE_ALLOWED_MIME_TYPES in app/core/config.py); the server
// stays authoritative and 413/415 responses are still handled when a deployment differs.
export const EVIDENCE_MAX_UPLOAD_BYTES = 26_214_400;
export const EVIDENCE_ALLOWED_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export class EvidenceApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string | null;
  readonly retryable: boolean;

  constructor(status: number, payload: Partial<EvidenceErrorPayload>) {
    super(payload.message || `Request failed with status ${status}`);
    this.name = 'EvidenceApiError';
    this.status = status;
    this.code = payload.code || 'EVIDENCE_REQUEST_FAILED';
    this.detail = payload.detail ?? null;
    this.retryable = Boolean(payload.retryable);
  }
}

export const isEvidenceApiError = (error: unknown): error is EvidenceApiError =>
  error instanceof EvidenceApiError;

/** Validate a file locally before any request is made. Returns null when the file is acceptable. */
export const precheckEvidenceFile = (file: File): EvidenceErrorPayload | null => {
  if (file.size > EVIDENCE_MAX_UPLOAD_BYTES) {
    const limitMb = Math.round(EVIDENCE_MAX_UPLOAD_BYTES / 1024 / 1024);
    return {
      code: 'EVIDENCE_FILE_TOO_LARGE',
      message: `"${file.name}" is larger than the ${limitMb} MB limit.`,
      retryable: false,
    };
  }
  if (!EVIDENCE_ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      code: 'EVIDENCE_UNSUPPORTED_TYPE',
      message: `"${file.name}" is not an allowed file type. Use PDF, PNG, JPEG, plain text, Word, Excel or PowerPoint documents.`,
      retryable: false,
    };
  }
  return null;
};

/**
 * Pull the `{code, message, detail, retryable}` payload out of an error body. The evidence
 * router returns the payload at the top level; a plain FastAPI HTTPException (for example the
 * inactive-account 403 raised before the evidence dependency runs) arrives as
 * `{detail: "message"}`, and a 422 validation error as `{detail: [...]}`. Accept all three, and
 * tolerate null/primitive bodies.
 */
const extractErrorPayload = (body: unknown): Partial<EvidenceErrorPayload> => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
  const record = body as Record<string, unknown>;
  if ('code' in record) return record as Partial<EvidenceErrorPayload>;
  const { detail } = record;
  if (typeof detail === 'string') return { message: detail };
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? (detail as Partial<EvidenceErrorPayload>)
    : {};
};

const parseErrorPayload = async (response: Response): Promise<Partial<EvidenceErrorPayload>> => {
  try {
    return extractErrorPayload(await response.json());
  } catch {
    return {};
  }
};

const evidenceRequest = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((init.headers as Record<string, string>) || {}),
  };
  const response = await fetch(resolveApiUrl(path), { ...init, headers });

  if (response.status === 401) {
    handleUnauthorized();
    throw new EvidenceApiError(401, { code: 'UNAUTHORIZED', message: 'Authentication required' });
  }
  if (!response.ok) {
    throw new EvidenceApiError(response.status, await parseErrorPayload(response));
  }
  return response.json() as Promise<T>;
};

const evidencePath = (taskId: string, evidenceId?: string): string =>
  `/api/v1/tasks/${encodeURIComponent(taskId)}/evidence${evidenceId ? `/${encodeURIComponent(evidenceId)}` : ''}`;

export interface ListEvidenceOptions {
  cursor?: string | null;
  limit?: number;
  includeDeleted?: boolean;
}

export const listEvidence = async (
  taskId: string,
  options: ListEvidenceOptions = {}
): Promise<EvidenceListResponse> => {
  const params = new URLSearchParams();
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.limit) params.set('limit', String(options.limit));
  if (options.includeDeleted) params.set('include_deleted', 'true');
  const query = params.toString();
  return evidenceRequest<EvidenceListResponse>(`${evidencePath(taskId)}${query ? `?${query}` : ''}`);
};

export const requestEvidenceAccess = (
  taskId: string,
  evidenceId: string,
  action: EvidenceAccessAction
): Promise<EvidenceAccessResponse> =>
  evidenceRequest<EvidenceAccessResponse>(`${evidencePath(taskId, evidenceId)}/access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });

export const deleteEvidence = (taskId: string, evidenceId: string): Promise<EvidenceDeleteResponse> =>
  evidenceRequest<EvidenceDeleteResponse>(evidencePath(taskId, evidenceId), { method: 'DELETE' });

const newClientUploadId = (): string => {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export interface UploadEvidenceOptions {
  description?: string;
  /** Idempotency key; generated when omitted. */
  clientUploadId?: string;
  /** Called with 0-100 as the browser sends the body. */
  onProgress?: (percent: number) => void;
}

/**
 * Upload one file. Uses XMLHttpRequest because `fetch` cannot report upload progress.
 */
export const uploadEvidence = (
  taskId: string,
  file: File,
  options: UploadEvidenceOptions = {}
): Promise<EvidenceUploadResponse> =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file, file.name);
    if (options.description) form.append('description', options.description);
    form.append('client_upload_id', options.clientUploadId || newClientUploadId());

    const xhr = new XMLHttpRequest();
    xhr.open('POST', resolveApiUrl(evidencePath(taskId)));
    const token = getAuthToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (options.onProgress && event.lengthComputable) {
        options.onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    const parseBody = (): unknown => {
      try {
        return xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        return {};
      }
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        handleUnauthorized();
        reject(new EvidenceApiError(401, { code: 'UNAUTHORIZED', message: 'Authentication required' }));
        return;
      }
      const body = parseBody();
      if (xhr.status >= 200 && xhr.status < 300) {
        options.onProgress?.(100);
        resolve(body as EvidenceUploadResponse);
        return;
      }
      reject(new EvidenceApiError(xhr.status, extractErrorPayload(body)));
    };
    xhr.onerror = () =>
      reject(
        new EvidenceApiError(0, {
          code: 'EVIDENCE_NETWORK_ERROR',
          message: 'Upload failed before reaching the server. Check your connection and retry.',
          retryable: true,
        })
      );
    xhr.onabort = () =>
      reject(new EvidenceApiError(0, { code: 'EVIDENCE_UPLOAD_ABORTED', message: 'Upload cancelled.' }));

    xhr.send(form);
  });
